import { Component, OnInit, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticationService } from 'src/app/services/auth/authentication.service';
import { CustomMessageService } from 'src/app/shared/components/custom-message/custom-message.service';
import { CustomMessageModel } from '@app/types/custom-message.types';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule
  ]
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  mfaForm: FormGroup;
  confirmPasswordForm: FormGroup;
  emailCodeForm: FormGroup;
  resetPasswordForm: FormGroup;
  resetPasswordConfirmForm: FormGroup;

  errorMessage: string = '';
  successMessage: string = '';
  isLoading: boolean = false;
  hidePassword: boolean = true;
  hideConfirmPassword: boolean = true;
  prompt: 'login' | 'mfaRequired' | 'confirmPassword' | 'emailCodeRequired' | 'resetPasswordRequest' | 'resetPasswordConfirm' = 'login';

  constructor(
    private fb: FormBuilder,
    private authenticationService: AuthenticationService,
    private router: Router,
    private zone: NgZone,
    private customMessageService: CustomMessageService
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });

    this.mfaForm = this.fb.group({
      code: ['', Validators.required]
    });

    this.confirmPasswordForm = this.fb.group({
      name: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });

    this.emailCodeForm = this.fb.group({
      code: ['', Validators.required]
    });

    this.resetPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.resetPasswordConfirmForm = this.fb.group({
      code: ['', Validators.required],
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.authenticationService.isLoggedIn().then((isAuth: boolean) => {
      if (isAuth) {
        this.router.navigate(['/home']);
      }
    });
  }

  passwordMatchValidator(form: FormGroup) {
    return form.get('password')?.value === form.get('confirmPassword')?.value
      ? null : { 'mismatch': true };
  }

    async login() {
    if (this.loginForm.invalid) return;
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const { email, password } = this.loginForm.value;
      let { authState } = await this.authenticationService.logIn(email, password);
      
      console.log('Authentication state received:', authState);

      this.zone.run(() => {
        if (authState === 'loginSuccess') {
          this.handleLoginSuccess();
        } else {
          if (authState === 'newPasswordRequired') {
            this.prompt = 'confirmPassword'; // CORRECCIÓN: Mapeo de estado
          } else {
            this.prompt = authState;
          }
        }
      });
    } catch (err) {
      console.error('Login error details:', err);
      this.zone.run(() => {
        this.errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      });
    } finally {
      this.zone.run(() => { this.isLoading = false; });
    }
  }

  async verifyCode() {
    const form = this.prompt === 'mfaRequired' ? this.mfaForm : this.emailCodeForm;
    if (form.invalid) return;
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const code = form.get('code')?.value;
      await this.authenticationService.sendVerificationCode(code);
      this.zone.run(() => this.handleLoginSuccess());
    } catch (err) {
      this.zone.run(() => { this.errorMessage = err instanceof Error ? err.message : 'Error desconocido'; });
    } finally {
      this.zone.run(() => { this.isLoading = false; });
    }
  }

  async confirmPass() {
    if (this.confirmPasswordForm.invalid) return;
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const { name, password } = this.confirmPasswordForm.value;
      const result = await this.authenticationService.confirmPassword(password, name);
      
      this.zone.run(() => {
        if (result.isSignedIn) {
          this.handleLoginSuccess();
        } else {
          this.prompt = 'emailCodeRequired'; // O el siguiente paso que devuelva Cognito
        }
      });
    } catch (err) {
      this.zone.run(() => { this.errorMessage = err instanceof Error ? err.message : 'Error desconocido'; });
    } finally {
      this.zone.run(() => { this.isLoading = false; });
    }
  }

  async requestPasswordReset() {
    if (this.resetPasswordForm.invalid) return;
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const { email } = this.resetPasswordForm.value;
      await this.authenticationService.forgotPassword(email);
      this.zone.run(() => {
        this.successMessage = 'Se ha enviado un código de verificación a tu correo.';
        this.prompt = 'resetPasswordConfirm';
      });
    } catch (err) {
      this.zone.run(() => { this.errorMessage = err instanceof Error ? err.message : 'Error desconocido'; });
    } finally {
      this.zone.run(() => { this.isLoading = false; });
    }
  }

  async confirmNewPassword() {
    if (this.resetPasswordConfirmForm.invalid) return;
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const { code, password } = this.resetPasswordConfirmForm.value;
      const username = this.resetPasswordForm.value.email;
      await this.authenticationService.confirmForgotPassword(username, code, password);
      this.zone.run(() => {
        this.successMessage = 'Contraseña cambiada con éxito. Por favor, inicia sesión.';
        this.prompt = 'login';
        this.loginForm.reset();
        this.resetPasswordConfirmForm.reset();
      });
    } catch (err) {
      this.zone.run(() => { this.errorMessage = err instanceof Error ? err.message : 'Error desconocido'; });
    } finally {
      this.zone.run(() => { this.isLoading = false; });
    }
  }

  goToResetPassword() {
    this.prompt = 'resetPasswordRequest';
    this.errorMessage = '';
    this.successMessage = '';
  }

  returnToLogin() {
    this.prompt = 'login';
    this.errorMessage = '';
    this.successMessage = '';
    this.loginForm.reset();
    this.mfaForm.reset();
    this.confirmPasswordForm.reset();
    this.emailCodeForm.reset();
    this.resetPasswordForm.reset();
    this.resetPasswordConfirmForm.reset();
  }

  private handleLoginSuccess() {
    const message: CustomMessageModel = {
      type: 'success',
      message: 'Inicio de sesión exitoso',
      title: 'Éxito',
      duration: 1500 // El mensaje se cerrará solo
    };
    this.customMessageService.open(message);
    this.router.navigate(['/home']);
  }
}